import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { BrandMark } from '@layerit/ui/components/brand-mark'
import { ThemeMenu } from '@layerit/ui/components/theme-menu'
import { Alert, AlertDescription } from '@layerit/ui/components/ui/alert'
import { Button } from '@layerit/ui/components/ui/button'
import { Field, FieldError, FieldLabel } from '@layerit/ui/components/ui/field'
import { Input } from '@layerit/ui/components/ui/input'
import { followLink } from '@web/lib/navigation'
import { ROUTES } from '@web/router'
import { useAuthFlow } from '@web/screens/auth/flow'
import { LegalLine, ProviderButtons } from '@web/screens/auth/pieces'
import type { EmailFormValues } from '@web/screens/auth/schema'
import { emailFormSchema } from '@web/screens/auth/schema'
import { publicSiteHref } from '@web/site'
import { APP_NAME } from '@layerit/brand'

interface SignUpScreenProps {
	error?: string | null
	redirectTo?: string
	navigate: (to: string) => void
}

export function SignUpScreen({ error = null, redirectTo = ROUTES.onboardingName, navigate }: SignUpScreenProps) {
	const flow = useAuthFlow({ redirectTo, verifyTo: ROUTES.signUpVerify, navigate, error })
	const wordmarkHref = publicSiteHref('/')

	const form = useForm<EmailFormValues>({
		resolver: zodResolver(emailFormSchema),
		defaultValues: { email: '' },
	})

	async function signUp({ email }: EmailFormValues) {
		await flow.sendCode(email)
	}

	const wordmark = (
		<>
			<BrandMark size={28} />
			<span className="text-sm font-semibold tracking-tight text-ink">{APP_NAME}</span>
		</>
	)

	return (
		<div className="flex min-h-screen flex-col bg-surface">
			<div className="flex shrink-0 items-center justify-between gap-4 px-6 py-6">
				{wordmarkHref ? (
					<a className="flex w-fit items-center gap-2.5 rounded-md transition-opacity hover:opacity-80" href={wordmarkHref}>
						{wordmark}
					</a>
				) : (
					<div className="flex w-fit items-center gap-2.5">{wordmark}</div>
				)}
				<ThemeMenu />
			</div>

			<div className="mx-auto flex w-full max-w-104 flex-1 flex-col justify-center px-6 pb-12">
				<div className="flex flex-col">
					<h1 className="mt-6 font-display text-2xl text-ink">One place to define every signature.</h1>
					<p className="mt-2.5 text-sm leading-relaxed text-ink-3">
						No password. We’ll email you a six-digit code to confirm your email address.
					</p>

					{flow.unavailable && (
						<p role="status" className="mt-6 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
							{APP_NAME} could not reach the server to load all sign-up options, so only email codes are shown. Reload once
							the API is reachable if you expected a provider button.
						</p>
					)}

					<div className="mt-8">
						<ProviderButtons methods={flow.methods} pending={flow.pending} redirectTo={redirectTo} layout="stack" />
					</div>

					{flow.methods.social.length > 0 && <hr className="mt-7 border-line" />}

					{flow.failure && (
						<Alert variant="destructive" className="mt-6">
							<AlertDescription>{flow.failure}</AlertDescription>
						</Alert>
					)}

					<form className="mt-7 flex flex-col" noValidate onSubmit={(event) => void form.handleSubmit(signUp)(event)}>
						<Controller
							control={form.control}
							name="email"
							render={({ field, fieldState }) => (
								<Field className="gap-2" data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor="email">Work email</FieldLabel>
									<Input
										{...field}
										id="email"
										type="email"
										autoComplete="email"
										required
										className="h-11"
										aria-invalid={fieldState.invalid}
									/>
									{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
								</Field>
							)}
						/>

						<LegalLine lead={`By continuing, I agree to ${APP_NAME}’s`} className="mt-7 text-left" />

						<Button type="submit" variant="primary" className="mt-4 h-11" disabled={flow.pending}>
							{flow.pending ? 'Sending…' : 'Sign up'}
						</Button>
					</form>

					<p className="mt-6 text-center text-xs leading-relaxed text-ink-3">
						Already have an account?{' '}
						<a
							className="font-medium text-ink-2 underline underline-offset-2 hover:text-ink"
							href={ROUTES.signIn}
							onClick={(event) => followLink(event, ROUTES.signIn, navigate)}
						>
							Sign in
						</a>
					</p>
				</div>
			</div>
		</div>
	)
}
